import { Anchor, Button, Card, Center, Group, Stack, Text, TextInput, Title } from '@mantine/core'
import { useEffect, useRef, useState, type FormEvent } from 'react'

import { useDocumentTitle } from '../components/PageHeader'
import { StatusBanner } from '../components/StatusBanner'
import { notifySaved } from '../lib/notify'
import { AuthApiError, type CodeSent } from './authApi'
import { useAuth } from './AuthContext'
import { describeAuthError, formatCountdown, formatWait } from './messages'

const CODE_LENGTH = 6

/** A code emailed to [email]. Times are epoch milliseconds, taken when the answer arrived. */
interface PendingCode {
  email: string
  expiresIn: number
  countdownFrom: number
  resendAt: number
}

function pendingCode(email: string, sent: CodeSent): PendingCode {
  const now = Date.now()
  return { email, expiresIn: sent.expiresIn, countdownFrom: now, resendAt: now + sent.resendAfter * 1000 }
}

/** Passwordless sign-in: an email address, then the 6-digit code the API emails to it. */
export function SignInScreen() {
  const [email, setEmail] = useState('')
  const [pending, setPending] = useState<PendingCode | null>(null)
  useDocumentTitle('Sign in')

  return (
    <Center mih="100vh" p="md">
      <Stack w="100%" maw={360} gap="lg">
        <Stack gap={4} align="center">
          <Title order={1} fz={40} lh="48px" fw={700} c="var(--mantine-primary-color-filled)" style={{ letterSpacing: '0.02em' }}>
            Macrotide
          </Title>
          <Text c="dimmed" ta="center">
            Nutrition and training, in one place.
          </Text>
        </Stack>
        <Card padding="lg">
          {pending ? (
            <CodeStep pending={pending} onPendingChange={setPending} onChangeEmail={() => setPending(null)} />
          ) : (
            <EmailStep
              email={email}
              onEmailChange={setEmail}
              onSent={(address, sent) => setPending(pendingCode(address, sent))}
            />
          )}
        </Card>
        <Group justify="center" gap="sm">
          <Anchor href="/privacy" size="xs" c="dimmed">
            Privacy
          </Anchor>
          <Anchor href="/terms" size="xs" c="dimmed">
            Terms
          </Anchor>
          <Anchor href="/cookies" size="xs" c="dimmed">
            Cookies
          </Anchor>
        </Group>
      </Stack>
    </Center>
  )
}

function EmailStep(props: {
  email: string
  onEmailChange: (email: string) => void
  onSent: (email: string, sent: CodeSent) => void
}) {
  const { startSignIn } = useAuth()
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const email = props.email.trim()
    setSending(true)
    setError(null)
    try {
      const sent = await startSignIn(email)
      props.onSent(email, sent)
    } catch (caught) {
      setError(describeAuthError(caught))
      setSending(false)
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)}>
      <Stack gap="md">
        <TextInput
          type="email"
          label="Email"
          description={`We'll email you a ${CODE_LENGTH}-digit code to sign in.`}
          value={props.email}
          onChange={(event) => props.onEmailChange(event.currentTarget.value)}
          autoComplete="email"
          autoFocus
          required
          size="md"
        />
        {error && <StatusBanner tone="error">{error}</StatusBanner>}
        <Button type="submit" size="md" loading={sending} fullWidth>
          Email me a code
        </Button>
      </Stack>
    </form>
  )
}

function CodeStep(props: {
  pending: PendingCode
  onPendingChange: (pending: PendingCode) => void
  onChangeEmail: () => void
}) {
  const { pending } = props
  const { startSignIn, verifyCode } = useAuth()
  const [code, setCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const inFlight = useRef(false)
  const input = useRef<HTMLInputElement>(null)

  const verify = async (value: string) => {
    if (inFlight.current) return
    if (value.length !== CODE_LENGTH) {
      setFieldError(`Enter the ${CODE_LENGTH}-digit code from the email.`)
      return
    }
    inFlight.current = true
    setVerifying(true)
    setFieldError(null)
    setError(null)
    setNotice(null)
    try {
      // On success the app replaces this screen; the toast still shows (Notifications sits outside it in main.tsx).
      const result = await verifyCode(pending.email, value)
      if (result.deletionCancelled) notifySaved('Your account deletion was cancelled. Welcome back.')
    } catch (caught) {
      inFlight.current = false
      setVerifying(false)
      setError(describeAuthError(caught))
      input.current?.focus()
      input.current?.select()
    }
  }

  // Keeps digits only, so a pasted "123 456" works, and submits as soon as all six are there.
  const changeCode = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, CODE_LENGTH)
    if (digits === code) return
    setCode(digits)
    setFieldError(null)
    if (digits.length === CODE_LENGTH) void verify(digits)
  }

  const resend = async () => {
    setResending(true)
    setError(null)
    setNotice(null)
    setFieldError(null)
    try {
      const sent = await startSignIn(pending.email)
      props.onPendingChange(pendingCode(pending.email, sent))
      setCode('')
      setNotice(`We sent a new code to ${pending.email}. Codes in earlier emails no longer work.`)
      input.current?.focus()
    } catch (caught) {
      if (caught instanceof AuthApiError && caught.status === 429 && caught.retryAfter !== null) {
        const now = Date.now()
        props.onPendingChange({ ...pending, countdownFrom: now, resendAt: now + caught.retryAfter * 1000 })
      }
      setError(describeAuthError(caught))
    } finally {
      setResending(false)
    }
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void verify(code)
  }

  return (
    <form onSubmit={submit}>
      <Stack gap="md">
        <Text size="sm">
          We emailed a {CODE_LENGTH}-digit code to{' '}
          <Text span fw={600} size="sm">
            {pending.email}
          </Text>
          . It expires in {formatWait(pending.expiresIn)}.
        </Text>
        <TextInput
          ref={input}
          label="Sign-in code"
          value={code}
          onChange={(event) => changeCode(event.currentTarget.value)}
          error={fieldError}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          readOnly={verifying}
          size="md"
          styles={{ input: { letterSpacing: '0.4em', textAlign: 'center', fontVariantNumeric: 'tabular-nums' } }}
        />
        {error && <StatusBanner tone="error">{error}</StatusBanner>}
        {notice && <StatusBanner tone="info">{notice}</StatusBanner>}
        <Button type="submit" size="md" loading={verifying} fullWidth>
          Sign in
        </Button>
        <Group justify="space-between" gap="xs">
          <ResendButton
            key={pending.resendAt}
            from={pending.countdownFrom}
            until={pending.resendAt}
            loading={resending}
            disabled={verifying}
            onClick={() => void resend()}
          />
          <Button variant="subtle" size="compact-sm" onClick={props.onChangeEmail} disabled={verifying || resending}>
            Use a different email
          </Button>
        </Group>
      </Stack>
    </form>
  )
}

/** "Resend code", disabled with a countdown until the server allows another code. */
function ResendButton(props: { from: number; until: number; loading: boolean; disabled: boolean; onClick: () => void }) {
  const [now, setNow] = useState(props.from)
  const waiting = now < props.until

  useEffect(() => {
    if (!waiting) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [waiting])

  return (
    <Button
      variant="subtle"
      size="compact-sm"
      loading={props.loading}
      disabled={props.disabled || waiting}
      onClick={props.onClick}
    >
      {waiting ? `Resend code in ${formatCountdown((props.until - now) / 1000)}` : 'Resend code'}
    </Button>
  )
}
